export interface TaxDocEditPatch {
  newName?: string;
  docType?: string;
  subType?: string | undefined;
  financialYearEnd?: number | null;
  property?: string | null;
}

export interface TaxDocSyncMessage {
  type: 'TAX_DOC_EDITED';
  taxDealId: string;
  documentId: string;
  patch: TaxDocEditPatch;
  /** Random per-tab id so a tab can ignore its own echo. */
  origin: string;
  at: number;
}

const CHANNEL_NAME = 'odin-tax-documents';

/** Stable per-tab origin id (survives re-renders, unique per window). */
export const TAB_ORIGIN_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

let channel: BroadcastChannel | null = null;

const getChannel = (): BroadcastChannel | null => {
  if (
    typeof window === 'undefined' ||
    typeof BroadcastChannel === 'undefined'
  ) {
    return null;
  }
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
};

/** Broadcast an edit to every other tab/window. No-op if unsupported. */
export const broadcastTaxDocEdit = (
  taxDealId: string,
  documentId: string,
  patch: TaxDocEditPatch,
): void => {
  const ch = getChannel();
  if (!ch) return;
  const message: TaxDocSyncMessage = {
    type: 'TAX_DOC_EDITED',
    taxDealId,
    documentId,
    patch,
    origin: TAB_ORIGIN_ID,
    at: Date.now(),
  };
  ch.postMessage(message);
};

/**
 * Subscribe to edits from OTHER tabs. Returns an unsubscribe fn.
 * Messages originating from this same tab are filtered out (we already applied
 * them locally via the mutation's optimistic update).
 */
export const subscribeTaxDocEdits = (
  handler: (msg: TaxDocSyncMessage) => void,
): (() => void) => {
  const ch = getChannel();
  if (!ch) return () => {};

  const listener = (event: MessageEvent<TaxDocSyncMessage>) => {
    const msg = event.data;
    if (!msg || msg.type !== 'TAX_DOC_EDITED') return;
    if (msg.origin === TAB_ORIGIN_ID) return;
    handler(msg);
  };

  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
};
